// Event frames — composing an event's configured frame onto a booth photo.
//
// Two kinds of frame exist:
//   • Polaroid (polaroid_portrait / polaroid_landscape): drawn from scratch by
//     composePolaroidImage — a coloured card with a thick caption strip.
//   • Custom (custom_frame): a transparent PNG the organiser uploads. The photo
//     sits underneath it and shows through the PNG's see-through area.
//
// A custom frame's shape and photo area are measured once, when the file is
// picked, and saved with the event — the booth never has to read pixels.

import {
  POLAROID_LAYOUTS,
  composePolaroidImage,
  drawFrameElements,
  drawImageCover,
  getEditorSize,
  loadImage,
} from './polaroidLayout';

export const CUSTOM_FRAME_ID = 'custom_frame';

// The overlay is saved inside the event's frame_elements list, which the API
// stores verbatim, next to the editor's text/logo elements.
export const FRAME_OVERLAY_TYPE = 'frame_overlay';

// Same print target as the Polaroid layouts: 10×15 cm at 300dpi.
const OUTPUT_LONG_SIDE = 1800;
// Smaller artwork than this prints visibly soft once stretched to that size.
const MIN_SHARP_LONG_SIDE = 1000;

// Pixels more transparent than this let the photo show through. Deliberately
// close to fully opaque: soft vignettes and tinted areas still count as photo,
// while a real frame border is solid in its core.
const SEE_THROUGH_BELOW_ALPHA = 250;

export class FrameOverlayError extends Error {
  constructor(code) {
    super(code);
    this.name = 'FrameOverlayError';
    this.code = code; // 'not_png' | 'unreadable' | 'no_transparency'
  }
}

export function getOrientation(width, height) {
  if (!width || !height) return null;
  const ratio = width / height;
  if (ratio > 1.05) return 'landscape';
  if (ratio < 0.95) return 'portrait';
  return 'square';
}

// Split a saved frame_elements list into the custom overlay (if any) and the
// editor's text/logo elements.
export function splitFrameElements(list) {
  const all = Array.isArray(list) ? list : [];
  return {
    overlay: all.find((el) => el && el.type === FRAME_OVERLAY_TYPE && el.src) || null,
    elements: all.filter((el) => el && el.type !== FRAME_OVERLAY_TYPE),
  };
}

// Shape of the area the photo has to fill — what the image model should aim for.
export function getPhotoRectOrientation(overlay) {
  if (!overlay) return null;
  const rect = overlay.photoRect || { w: 1, h: 1 };
  return getOrientation(rect.w * overlay.width, rect.h * overlay.height);
}

// Read a picked file, bring it down to print size, and find where the photo goes.
export async function prepareFrameOverlay(file) {
  const looksLikePng =
    /^image\/(png|webp)$/i.test(file?.type || '') || /\.(png|webp)$/i.test(file?.name || '');
  if (!looksLikePng) throw new FrameOverlayError('not_png');

  const dataUrl = await readFileAsDataUrl(file);
  let img;
  try {
    img = await loadImage(dataUrl);
  } catch {
    throw new FrameOverlayError('unreadable');
  }

  const naturalWidth = img.naturalWidth || img.width;
  const naturalHeight = img.naturalHeight || img.height;
  if (!naturalWidth || !naturalHeight) throw new FrameOverlayError('unreadable');

  const scale = Math.min(1, OUTPUT_LONG_SIDE / Math.max(naturalWidth, naturalHeight));
  const width = Math.round(naturalWidth * scale);
  const height = Math.round(naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, width, height);

  const photoRect = findPhotoRect(ctx.getImageData(0, 0, width, height).data, width, height);
  if (!photoRect) throw new FrameOverlayError('no_transparency');

  return {
    type: FRAME_OVERLAY_TYPE,
    // Oversized artwork is re-encoded at print size; anything smaller is kept
    // byte-for-byte so the organiser's file isn't touched.
    src: scale < 1 ? canvas.toDataURL('image/png') : dataUrl,
    name: file.name || '',
    width,
    height,
    photoRect,
    lowResolution: Math.max(naturalWidth, naturalHeight) < MIN_SHARP_LONG_SIDE,
  };
}

// The photo goes into the frame's biggest see-through region. A hole enclosed by
// the frame wins over open areas that touch the edge (transparent rounded
// corners, margins around a card) when it is photo-sized; an overlay that is
// see-through almost everywhere has no such hole, so the photo fills its open
// area instead. Returns that region's bounding box as fractions of the image, or
// null when too little of the image is see-through to hold a photo.
function findPhotoRect(rgba, width, height) {
  const total = width * height;
  const visited = new Uint8Array(total);
  for (let i = 0; i < total; i++) {
    // Opaque pixels are frame — never part of a region.
    if (rgba[i * 4 + 3] >= SEE_THROUGH_BELOW_ALPHA) visited[i] = 1;
  }

  const stack = new Int32Array(total);
  let largest = null;
  let largestEnclosed = null;

  for (let start = 0; start < total; start++) {
    if (visited[start]) continue;
    visited[start] = 1;
    stack[0] = start;
    let size = 1;
    let area = 0;
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    let enclosed = true;

    while (size > 0) {
      const i = stack[--size];
      const x = i % width;
      const y = (i - x) / width;
      area++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (x === 0 || y === 0 || x === width - 1 || y === height - 1) enclosed = false;

      if (x > 0 && !visited[i - 1]) { visited[i - 1] = 1; stack[size++] = i - 1; }
      if (x < width - 1 && !visited[i + 1]) { visited[i + 1] = 1; stack[size++] = i + 1; }
      if (y > 0 && !visited[i - width]) { visited[i - width] = 1; stack[size++] = i - width; }
      if (y < height - 1 && !visited[i + width]) { visited[i + width] = 1; stack[size++] = i + width; }
    }

    const region = { area, minX, minY, maxX, maxY };
    if (!largest || area > largest.area) largest = region;
    if (enclosed && (!largestEnclosed || area > largestEnclosed.area)) largestEnclosed = region;
  }

  let pick = largest;
  if (
    largestEnclosed &&
    largestEnclosed.area >= total * 0.05 &&
    largestEnclosed.area >= largest.area * 0.35
  ) {
    pick = largestEnclosed;
  }
  if (!pick || pick.area < total * 0.02) return null;

  return {
    x: pick.minX / width,
    y: pick.minY / height,
    w: (pick.maxX - pick.minX + 1) / width,
    h: (pick.maxY - pick.minY + 1) / height,
  };
}

// Photo underneath, the organiser's PNG on top, then any text/logo elements from
// the editor. Output keeps the frame's own shape at print size.
export async function composeCustomFrameImage({ baseImageSrc, overlay, elements = [] }) {
  const scale = OUTPUT_LONG_SIDE / Math.max(overlay.width, overlay.height);
  const W = Math.round(overlay.width * scale);
  const H = Math.round(overlay.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // White only shows where the PNG is see-through outside the photo area
  // (rounded corners and the like) — blank paper, as it would print.
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, W, H);

  const [photo, frame] = await Promise.all([loadImage(baseImageSrc), loadImage(overlay.src)]);
  const rect = overlay.photoRect || { x: 0, y: 0, w: 1, h: 1 };
  await drawImageCover(ctx, photo, rect.x * W, rect.y * H, rect.w * W, rect.h * H);
  ctx.drawImage(frame, 0, 0, W, H);

  const editor = getEditorSize(overlay.width, overlay.height);
  await drawFrameElements(ctx, elements, {
    width: W,
    height: H,
    editorWidth: editor.width,
    editorHeight: editor.height,
    frameColor: '#FFFFFF',
  });

  return canvas.toDataURL('image/jpeg', 0.95);
}

// Compose whatever frame an event is configured with onto a photo. Resolves to
// null when the event has no frame the booth can draw, so the caller keeps the
// photo as it is.
export async function composeEventFrameImage({ baseImageSrc, frameId, frameColor, frameElements }) {
  const { overlay, elements } = splitFrameElements(frameElements);
  if (frameId === CUSTOM_FRAME_ID && overlay) {
    return composeCustomFrameImage({ baseImageSrc, overlay, elements });
  }

  const layout = POLAROID_LAYOUTS[frameId];
  if (!layout) return null;
  const editor = getEditorSize(layout.outputWidth, layout.outputHeight);
  return composePolaroidImage({
    baseImageSrc,
    layoutId: frameId,
    frameColor: frameColor || '#FFFFFF',
    elements,
    editorWidth: editor.width,
    editorHeight: editor.height,
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new FrameOverlayError('unreadable'));
    reader.readAsDataURL(file);
  });
}
